import { each, escape, range } from "lodash";
import { fileUpload, UploadProgressData, UploadResult, UploadProgressHandler } from "./file-upload";

declare const jQuery: any;
const $ = jQuery;

export interface FileUploadWidgetSettings
{
	renderUploadButton?: boolean; // default true
	uploadHandlerName?: string; // if not set, upload button not rendered
	maxFiles?: number; // default 1
	checkFiles?: (files: File[]) => boolean; // return false to abort upload
	onUploadStart?: (files: File[]) => void;
	onUploadSuccess?: (files: File[], serverResponse: any) => void;
	onUploadError?: (files: File[], reason: any) => void;
	onUploadComplete?: (files: File[]) => void;
	onChange?: (files?: File[]) => void;
	onProgress?: UploadProgressHandler;
	getUploadData?: (files?: File[]) => any;
	chooseFileText?: string;
}

// Returns jQuery object
function createFileUploadWidget(settings?: FileUploadWidgetSettings)
{
	settings = settings || {};
	const $outerWrap = $(`<div class="file-upload-widget">`);
	const $innerWrap = $(`<div>`).appendTo($outerWrap);
	let $fileInputs = $();
	const maxFiles: number = settings.maxFiles || 1;
	each(range(0, maxFiles), (index: number) => {
		const $fileInput = $(`<input type="file" style="display:none;" />`).appendTo($innerWrap);
		$fileInputs = $fileInputs.add($fileInput);
	});
	const $list = $(`<div class="cf-file-list">`).appendTo($innerWrap);
	const chooseFileText: string = settings.chooseFileText || "Choose File";
	const $chooseFileBtn = $(`<div class="cf-file-input-label-button"><div>${chooseFileText}</div></div>`).appendTo($innerWrap);
	const $maxReached = $(`<div class="cf-file-list-max-reached">Maximum number of files reached</div>`).appendTo($innerWrap);
	let $uploadButton = undefined;
	const onChange = (typeof settings.onChange === "function") ?
		() => {
			settings.onChange(getFiles());
		} :
		(() => {});

	const getEmptyFileInput = () => {
		let $input: any = undefined;
		$fileInputs.each(function () {
			const fileData = this.files[0];
			if (!fileData)
			{
				$input = $(this);
				return false;
			}
		});
		return $input;
	};
	
	const removeListItem = (index: number) => {
		$list.find(`[data-index="${index}"]`).remove();
		onChange();
	};
	
	const resetFileInput = (fileInput) => {
		fileInput.value = "";
		fileInput.type = "";
		fileInput.type = "file";
		onChange();
	};

	// clears/resets file inputs & list
	const clearAllFiles = () => {
		$fileInputs.each(function (index: number) {
			resetFileInput(this);
			removeListItem(index);
		});
	};
	
	const clearListItemsForEmptyInputs = () => {
		$fileInputs.each(function (index: number) {
			if (!this.files[0])
			{
				removeListItem(index);
			}
		});
	};

	const addListItem = (index: number, fileData): void => {
		const name: string = escape(fileData.name);
		const html = `<div class="cf-file-list-item" data-index="${index}"><div>${name}</div></div>`;
		const $item = $(html);
		const $removeBtn = $(`<div class="cf-file-list-item-remove" title="Click to remove file"></div>`);
		$removeBtn.on("click", () => {
			removeListItem(index);
			resetFileInput($fileInputs[index]);
			updateChooseFileButtonDisabledState();
			updateMaxReachedState();
		});
		$item.append($removeBtn);
		$list.append($item);
		onChange();
	};

	const updateChooseFileButtonDisabledState = () => {
		if (getEmptyFileInput())
		{
			$chooseFileBtn.removeClass("disabled");
		}
		else
		{
			$chooseFileBtn.addClass("disabled");
		}
	};

	const updateUploadButtonDisabledState = () => {
		if (!$uploadButton)
		{
			return;
		}
		if (getFiles().length > 0)
		{
			$uploadButton.removeAttr("disabled");
		}
		else
		{
			$uploadButton.attr("disabled", "disabled");
		}
	};

	const updateMaxReachedState = () => {
		if (getEmptyFileInput())
		{
			$maxReached.removeClass("show");
		}
		else if (maxFiles > 1)
		{
			$maxReached.addClass("show");
		}
	};

	const onUploadComplete = () => {
		clearAllFiles();
		clearListItemsForEmptyInputs();
		updateChooseFileButtonDisabledState();
		updateUploadButtonDisabledState();
		updateMaxReachedState();
	};

	const getFiles = (): File[] => {
		const files: File[] = [];
		$fileInputs.each(function () {
			each(this.files, (file: File) => {
				files.push(file);
			});
		});
		return files;
	};

	$fileInputs.each(function (index: number) {
		const fileInput = this;
		const fileData = fileInput.files[0];
		if (fileData)
		{
			addListItem(index, fileData);
		}
		$(fileInput).on("change", function () {
			const fileData = fileInput.files[0];
			removeListItem(index);
			if (fileData)
			{
				addListItem(index, fileData);
				updateChooseFileButtonDisabledState();
				updateUploadButtonDisabledState();
				updateMaxReachedState();
			}
		});
	});

	if ((settings.renderUploadButton || settings.renderUploadButton === undefined) && settings.uploadHandlerName)
	{
		$uploadButton = $(`<div class="button upload-button">Upload</div>`).click(e => {
			if ($uploadButton.is(`[disabled]`))
			{
				return false;
			}
			const files: File[] = getFiles();
			if (!files.length)
			{
				return false;
			}
			if (settings.checkFiles && !settings.checkFiles(files))
			{
				return false;
			}
			$uploadButton.attr("disabled", "disabled");
			const uploadData: any = settings.getUploadData ? settings.getUploadData(files) : undefined;
			if (settings.onUploadStart)
			{
				settings.onUploadStart(files);
			}
			fileUpload(files, settings.uploadHandlerName, uploadData, settings.onProgress).
			then((res: UploadResult) => {
				$uploadButton.removeAttr("disabled");
				clearAllFiles();
				if (settings.onUploadSuccess)
				{
					settings.onUploadSuccess(files, res);
				}
				if (settings.onUploadComplete)
				{
					settings.onUploadComplete(files);
				}
			}).
			catch((reason) => {
				$uploadButton.removeAttr("disabled");
				if (settings.onUploadError)
				{
					settings.onUploadError(files, reason);
				}
				if (settings.onUploadComplete)
				{
					settings.onUploadComplete(files);
				}
			});
			return false;
		});
		const $uploadButtonWrap = $(`<div class="upload-button-wrap">`).append($uploadButton);
		$innerWrap.append($uploadButtonWrap);
	}

	updateChooseFileButtonDisabledState();
	updateUploadButtonDisabledState();
	updateMaxReachedState();

	$chooseFileBtn.on("click", function () {
		const $fileInput = getEmptyFileInput();
		if ($fileInput)
		{
			$fileInput.click();
		}
	});

	$outerWrap.on("reset", function () {
		clearAllFiles();
	});

	return $outerWrap;
}

if (!window["iw"])
{
	window["iw"] = {};
}

window["iw"].createFileUploadWidget = createFileUploadWidget;
